/**
 * Intake Referrals field behaviour.
 *
 * - "Find intake" is a combobox filtering the curated dictionary embedded in the
 *   page as JSON. Matches are capped so the DOM stays small no matter how large
 *   the dictionary is, and the full list is only offered on focus when the
 *   dictionary is small enough to browse (see data-browse-threshold).
 * - Selecting an intake clones the selected-row template, substitutes its
 *   placeholders, and drops the detail rows that have no value.
 * - Removal is origin-aware: an intake Emma already has attached
 *   (data-prefilled) is kept in the DOM with action=remove so Emma is told to
 *   detach it, while one added in this session is simply dropped. The server
 *   re-derives data-prefilled on every render, so it cannot be forged.
 *   A row marked for removal deliberately stays excluded from the picker: its
 *   `[code]` input is still in the DOM, so selectedCodes() still counts it.
 *   Re-offering the code would let a member post `select` and `remove` for the
 *   same intake, and a second identical well would read as a bug while the first
 *   is still on screen. Undo is the affordance, and it sits on the row itself.
 * - "Add a new one" clones the net-new template.
 * - Requesting a correction reveals the intake's current values as editable
 *   fields. The member edits what is wrong rather than describing it; the
 *   checkbox posts an explicit `requested` flag because prefilled fields cannot
 *   signal intent by being non-empty.
 *
 * Row indices are assigned here and carry no meaning — the server iterates the
 * posted values and ignores them.
 */

interface Intake {
  code: string;
  label: string;
  phone: string;
  fax: string;
  website: string;
  standardFormUrl: string;
  furtherInstructions: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const readDictionary = (field: HTMLElement): Intake[] => {
  const script = field.querySelector<HTMLScriptElement>(
    ".js-intake-referrals-data"
  );
  if (!script?.textContent) {
    return [];
  }

  try {
    const parsed = JSON.parse(script.textContent);
    return Array.isArray(parsed) ? (parsed as Intake[]) : [];
  } catch {
    return [];
  }
};

/** Codes already present as rows, so the picker can exclude them. */
const selectedCodes = (rows: HTMLElement): Set<string> => {
  const codes = new Set<string>();
  rows
    .querySelectorAll<HTMLInputElement>('input[name*="[code]"]')
    .forEach((input) => {
      if (input.value) {
        codes.add(input.value);
      }
    });
  return codes;
};

const attachField = (field: HTMLElement): void => {
  const rows = field.querySelector<HTMLElement>(".js-intake-rows");
  const selectedTemplate = field.querySelector<HTMLTemplateElement>(
    ".js-intake-selected-template"
  );
  const newTemplate = field.querySelector<HTMLTemplateElement>(
    ".js-intake-new-template"
  );
  const search = field.querySelector<HTMLInputElement>(".js-intake-search");
  const listbox = field.querySelector<HTMLElement>(".js-intake-listbox");
  const combobox = field.querySelector<HTMLElement>(
    ".intake-referrals__combobox"
  );
  const status = field.querySelector<HTMLElement>(".js-intake-status");
  const addNewButton = field.querySelector<HTMLElement>(".js-intake-add-new");

  if (
    !rows ||
    !selectedTemplate ||
    !newTemplate ||
    !search ||
    !listbox ||
    !combobox ||
    !status ||
    !addNewButton
  ) {
    return;
  }

  const dictionary = readDictionary(field);
  const browseThreshold = Number(field.dataset.browseThreshold ?? "100");
  const maxResults = Number(field.dataset.maxResults ?? "50");

  // Continue past any server-rendered rows so indices never collide.
  let nextIndex =
    Array.from(rows.querySelectorAll<HTMLElement>(".js-intake-row")).reduce(
      (highest, row) => Math.max(highest, Number(row.dataset.index ?? "-1")),
      -1
    ) + 1;

  let matches: Intake[] = [];
  let activeIndex = -1;

  const closeList = (): void => {
    combobox.classList.remove("is-open");
    search.setAttribute("aria-expanded", "false");
    listbox.innerHTML = "";
    matches = [];
    activeIndex = -1;
  };

  const renderMatches = (): void => {
    listbox.innerHTML = matches
      .map(
        (intake, index) =>
          `<li class="field__dropdown-item js-intake-option" role="option"` +
          ` id="${escapeHtml(field.id || "intake")}-option-${index}"` +
          ` data-code="${escapeHtml(intake.code)}"` +
          ` aria-selected="false">${escapeHtml(intake.label)}</li>`
      )
      .join("");

    combobox.classList.toggle("is-open", matches.length > 0);
    search.setAttribute("aria-expanded", String(matches.length > 0));
  };

  const setActive = (index: number): void => {
    const options = listbox.querySelectorAll<HTMLElement>(".js-intake-option");
    options.forEach((option) => option.setAttribute("aria-selected", "false"));

    activeIndex = index;
    const active = options[index];
    if (active) {
      active.setAttribute("aria-selected", "true");
      active.scrollIntoView({ block: "nearest" });
    }
  };

  const setStatus = (message: string): void => {
    status.textContent = message;
    status.classList.toggle("hidden", message === "");
  };

  const search_ = (query: string, viaFocus: boolean): void => {
    const trimmed = query.trim().toLowerCase();
    const taken = selectedCodes(rows);
    const available = dictionary.filter((intake) => !taken.has(intake.code));

    // Only a dictionary too large to skim is worth gating behind typed input.
    // At the real scale (~116) browsing is useful, so the threshold sits well
    // above it and this stays false.
    const tooManyToBrowse = available.length > browseThreshold;

    if (trimmed.length === 0) {
      if (!viaFocus || tooManyToBrowse) {
        closeList();
        setStatus(
          viaFocus && tooManyToBrowse
            ? `${available.length} intakes available — start typing to search.`
            : ""
        );
        return;
      }
    } else if (trimmed.length < 2 && tooManyToBrowse) {
      closeList();
      setStatus("Keep typing to search.");
      return;
    }

    const found =
      trimmed.length === 0
        ? available
        : available.filter((intake) =>
            intake.label.toLowerCase().includes(trimmed)
          );

    matches = found.slice(0, maxResults);

    // Never truncate silently: a capped list looks identical to a complete one,
    // so a member could conclude their intake simply isn't listed.
    if (found.length === 0) {
      setStatus("No matching intake found.");
    } else if (found.length > matches.length) {
      setStatus(
        `Showing the first ${matches.length} of ${found.length} — keep typing to narrow.`
      );
    } else {
      setStatus("");
    }

    renderMatches();
  };

  /** Substitute the clone template's placeholders and drop empty details. */
  const addSelected = (intake: Intake): void => {
    if (selectedCodes(rows).has(intake.code)) {
      return;
    }

    const html = selectedTemplate.innerHTML
      .replace(/__INDEX__/g, String(nextIndex))
      .replace(/__CODE__/g, escapeHtml(intake.code))
      .replace(/__LABEL__/g, escapeHtml(intake.label))
      .replace(/__PHONE__/g, escapeHtml(intake.phone ?? ""))
      .replace(/__FAX__/g, escapeHtml(intake.fax ?? ""))
      .replace(/__WEBSITE__/g, escapeHtml(intake.website ?? ""))
      .replace(/__FORM_URL__/g, escapeHtml(intake.standardFormUrl ?? ""))
      .replace(
        /__INSTRUCTIONS__/g,
        escapeHtml(intake.furtherInstructions ?? "")
      );

    nextIndex += 1;

    const holder = document.createElement("div");
    holder.innerHTML = html;

    const row = holder.querySelector<HTMLElement>(".js-intake-row");
    if (!row) {
      return;
    }

    // Details whose value was empty render as a label with no value — drop them.
    row
      .querySelectorAll<HTMLElement>(".intake-referrals__detail")
      .forEach((detail) => {
        if (!detail.querySelector("dd")?.textContent?.trim()) {
          detail.remove();
        }
      });

    rows.append(row);
  };

  const addNew = (): void => {
    const holder = document.createElement("div");
    holder.innerHTML = newTemplate.innerHTML.replace(
      /__INDEX__/g,
      String(nextIndex)
    );
    nextIndex += 1;

    const row = holder.querySelector<HTMLElement>(".js-intake-row");
    if (!row) {
      return;
    }

    rows.append(row);
    row.querySelector<HTMLInputElement>('input[name*="[name]"]')?.focus();
  };

  const markRemoved = (row: HTMLElement, removed: boolean): void => {
    const action = row.querySelector<HTMLInputElement>(".js-intake-action");
    const notice = row.querySelector<HTMLElement>(".js-intake-removed-notice");
    const correction = row.querySelector<HTMLElement>(
      ".js-intake-correction-wrapper"
    );

    if (action) {
      action.value = removed ? "remove" : "select";
    }
    row.classList.toggle("is-removed", removed);
    notice?.classList.toggle("hidden", !removed);
    correction?.classList.toggle("hidden", removed);

    if (removed) {
      // A correction is meaningless on an intake being detached, so untick it.
      // Unticking is enough: an unchecked box posts nothing, so
      // `[correction][requested]` disappears and the builder emits no
      // correction. The field *values* are deliberately left alone — they are
      // prefilled with the intake's current details, and clearing them would
      // destroy the member's edits if they hit Undo. Same policy as the untick
      // handler below.
      const toggle = row.querySelector<HTMLInputElement>(
        ".js-intake-correction-toggle"
      );
      if (toggle) {
        toggle.checked = false;
        toggle.setAttribute("aria-expanded", "false");
      }
      row
        .querySelector<HTMLElement>(".intake-referrals__correction-body")
        ?.classList.add("hidden");
    }
  };

  // Delegated so cloned rows work without re-binding.
  rows.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const row = target.closest<HTMLElement>(".js-intake-row");
    if (!row) {
      return;
    }

    if (target.closest(".js-intake-remove")) {
      // Emma already holds this one, so it must be told to detach it; a row
      // added in this session is unknown to Emma and can just go.
      if (row.dataset.prefilled === "1") {
        markRemoved(row, true);
      } else {
        row.remove();
      }
      return;
    }

    if (target.closest(".js-intake-undo-remove")) {
      markRemoved(row, false);
    }
  });

  rows.addEventListener("change", (event) => {
    const toggle = (event.target as HTMLElement).closest<HTMLInputElement>(
      ".js-intake-correction-toggle"
    );
    if (!toggle) {
      return;
    }

    const row = toggle.closest<HTMLElement>(".js-intake-row");
    const body = row?.querySelector<HTMLElement>(
      ".intake-referrals__correction-body"
    );
    body?.classList.toggle("hidden", !toggle.checked);
    toggle.setAttribute("aria-expanded", String(toggle.checked));

    // Deliberately does NOT clear the fields on untick. They are prefilled with
    // the intake's current values, so clearing would destroy the member's edits
    // and leave them retyping from scratch if they re-tick. Intent is carried by
    // the checkbox itself (it posts `[correction][requested]`), which the XML
    // builder requires before emitting anything — so leaving values in place is
    // harmless.
  });

  search.addEventListener("input", () => search_(search.value, false));
  search.addEventListener("focus", () => search_(search.value, true));

  search.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeList();
      return;
    }

    if (!matches.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((activeIndex + 1) % matches.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((activeIndex - 1 + matches.length) % matches.length);
    } else if (event.key === "Enter") {
      // Never let the combobox submit the form.
      event.preventDefault();
      const chosen = matches[activeIndex >= 0 ? activeIndex : 0];
      if (chosen) {
        addSelected(chosen);
        search.value = "";
        closeList();
      }
    }
  });

  listbox.addEventListener("click", (event) => {
    const option = (event.target as HTMLElement).closest<HTMLElement>(
      ".js-intake-option"
    );
    if (!option) {
      return;
    }

    const intake = dictionary.find(
      (candidate) => candidate.code === option.dataset.code
    );
    if (intake) {
      addSelected(intake);
      search.value = "";
      closeList();
    }
  });

  document.addEventListener("click", (event) => {
    if (!combobox.contains(event.target as Node)) {
      closeList();
    }
  });

  addNewButton.addEventListener("click", addNew);
};

const initialize = (): void =>
  document
    .querySelectorAll<HTMLElement>(".js-intake-referrals")
    .forEach((field) => {
      if (field.dataset.initialized) {
        return;
      }
      field.dataset.initialized = "true";

      attachField(field);
    });

initialize();

window.addEventListener("htmx:afterSwap", initialize);

export default initialize;
